/**
 * Long-press "mark missed" for habit cells and Today targets.
 *
 * Mouse users already have the context menu; touch and pen users need an
 * equivalent that does not interfere with ordinary taps or scrolling. One
 * tracker serves a whole page: presses are keyed by the cell they started on.
 */

export const LONG_PRESS_DURATION_MS = 600;
export const LONG_PRESS_MOVE_THRESHOLD_PX = 10;
/**
 * How long the click that follows a long press is swallowed, at most: the next
 * pointerdown ends it sooner.
 */
const SUPPRESS_CLICK_MS = 1000;

export function createLongPressTracker() {
  let timer: ReturnType<typeof setTimeout> | undefined;
  let active: { key: string; pointerId: number; x: number; y: number } | undefined;
  let suppressed: { key: string; expiresAt: number } | undefined;

  function clear() {
    if (timer !== undefined) clearTimeout(timer);
    timer = undefined;
    active = undefined;
  }

  function suppressNextClick(key: string) {
    // Browsers dispatch click after pointerup. Ignore that one click so a
    // long press does not immediately overwrite the missed status.
    suppressed = { key, expiresAt: Date.now() + SUPPRESS_CLICK_MS };
  }

  function isSuppressed(key: string) {
    return suppressed?.key === key && Date.now() <= suppressed.expiresAt;
  }

  return {
    /** Starts timing a press on `key`; `onLongPress` runs if it is held. */
    pointerDown(event: PointerEvent, key: string, onLongPress: () => void) {
      // The hold's own click always arrives before the next press begins, so a
      // pointerdown means nothing is left to swallow. Without this, a browser
      // that fires no click after the hold (only contextmenu) would swallow a
      // quick tap on the same target for up to a second. A second finger
      // landing mid-hold doesn't count: the first one's click is still to come.
      if (event.isPrimary !== false) suppressed = undefined;
      if (event.pointerType === 'mouse' || event.isPrimary === false) return;
      clear();
      active = { key, pointerId: event.pointerId, x: event.clientX, y: event.clientY };
      timer = setTimeout(() => {
        const press = active;
        timer = undefined;
        if (!press) return;
        suppressNextClick(press.key);
        onLongPress();
      }, LONG_PRESS_DURATION_MS);
    },

    /** A press that drifts more than a few pixels is a scroll, not a hold. */
    pointerMove(event: PointerEvent) {
      if (!active || active.pointerId !== event.pointerId) return;
      if (
        Math.hypot(event.clientX - active.x, event.clientY - active.y) >
        LONG_PRESS_MOVE_THRESHOLD_PX
      ) {
        clear();
      }
    },

    /** pointerup / pointercancel / pointerleave. */
    pointerEnd(event: PointerEvent) {
      if (active?.pointerId === event.pointerId) clear();
    },

    /**
     * Whether the click that follows a long press should be ignored. Consumes
     * the suppression: call it at the start of a click handler.
     */
    consumeClick(key: string): boolean {
      const swallow = isSuppressed(key);
      suppressed = undefined;
      return swallow;
    },

    /**
     * Right-click, or the contextmenu some touch and pen browsers emit after a
     * hold. Calls `onMissed` unless the long-press timer already did.
     */
    contextMenu(event: MouseEvent, key: string, onMissed: () => void) {
      event.preventDefault();
      if (active?.key === key) {
        const alreadyHandled = timer === undefined;
        clear();
        suppressNextClick(key);
        // The long-press timer already updated the cell, so don't toggle it again.
        if (alreadyHandled) return;
        onMissed();
        return;
      }
      if (isSuppressed(key)) return;
      suppressed = undefined;
      onMissed();
    },

    destroy: clear,
  };
}
